import { FormBase } from "commonAssets/components/FormBase";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { loadMember } from "commonAssets/lib/loadMember";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { I18n } from "commonAssets/i18n";

export class OptOut extends FormBase {
    constructor(
        data,
        language,
        i18n,
        enableClaimFields = true,
        enableClaimTransactions = true,
        errorHandler = new ErrorHandler()
    ) {
        super(data, language, i18n);
        this.enableClaimFields = enableClaimFields;
        this.enableClaimTransactions = enableClaimTransactions;
        this.errorHandler = errorHandler;
    }

    async form() {
        try {
            const response = await fetch(this.data);
            const json = await FormBase.resolveHtmlFields(
                await response.json(),
                { baseUrl: response.url }
            );
            const form = json.claim_form[this.lang];
            this.init_exclusion(form);
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Error fetching claim form", { cause: error })
            );
            this.errorHandler.display_modal(error);
        }
    }

    init_exclusion(form) {
        this.exclusion.api
            .exclusionDeadlineGet({ domain: this.exclusion.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let exclusion_deadline;
                if (Boolean(result.data.exclusion_deadline)) {
                    exclusion_deadline = result.data.exclusion_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                if (form.form_options.exclusion_form.guard.display) {
                    // Init opt out guard
                    this.init_exclusion_guard(
                        form,
                        container,
                        datetime,
                        exclusion_deadline
                    );
                } else {
                    // New Exclusion Form
                    container.appendChild(this.exclusion_form(form));
                    outputDiv.appendChild(container);

                    // Initiate opt out fields
                    this.init_exclusion_fields();

                    // Submit form
                    document
                        .getElementById("form")
                        .addEventListener("submit", (process) => {
                            this.process_exclusion(
                                process,
                                this.exclusion,
                                exclusion_deadline
                            );
                        });
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching exclusion deadline", {
                        cause: error,
                    })
                );
                this.errorHandler.display(error);
            });
    }

    init_exclusion_fields(params) {
        this.exclusion.inputs = Array.from(
            document.querySelectorAll(
                "#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url"
            )
        );
        this.exclusion.inputs.transactions = Array.from(
            document.querySelectorAll(
                "#transactions input, #transactions textarea, #transactions select"
            )
        );

        this.exclusion.inputs.forEach(function (field) {
            // Check for validation attribute
            if (field.hasAttribute("data-validate")) {
                // Attach change event handler for validation
                field.addEventListener("change", function (e) {
                    validateField(field);
                });
            }
        });

        if (
            this.exclusion.inputs.transactions &&
            this.exclusion.inputs.transactions.length > 0
        ) {
            this.exclusion.inputs.transactions.forEach(function (field) {
                // Check for validation attribute
                if (field.hasAttribute("data-validate")) {
                    // Attach change event handler for validation
                    field.addEventListener("change", function (e) {
                        validateField(field);
                    });
                }
            });
        }

        // Validate phone number
        this.setup_phone_number_validation();

        // Country field toggle
        this.setup_country_field_toggle();

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("init");
        }
    }

    init_exclusion_guard(form, container, datetime, exclusion_deadline) {
        // Guard
        container.appendChild(this.guard(form, datetime));
        outputDiv.appendChild(container);

        const zero_zero_id = document.getElementById("zero_zero_id");
        const confirmation_code = document.getElementById("confirmation_code");
        const skip_guard = document.getElementById("skip-guard");

        // Notice Id and Confirmation Code
        [zero_zero_id, confirmation_code].forEach(function (input) {
            input.addEventListener("change", validateField);
        });

        // Skip Guard
        if (skip_guard) {
            skip_guard.addEventListener("click", () => {
                // Hide guard
                document.getElementById("guard").classList.add("d-none");
                // New Claim Form
                container.appendChild(this.exclusion_form(form));
                container.scrollIntoView({ behavior: "smooth" });
                outputDiv.appendChild(container);

                // Initiate opt out fields
                this.init_exclusion_fields();

                // Submit form
                document
                    .getElementById("form")
                    .addEventListener("submit", (process) => {
                        this.process_exclusion(
                            process,
                            this.exclusion,
                            exclusion_deadline
                        );
                    });
            });
        }

        // Validate Guard Fields
        document.getElementById("guard").addEventListener("submit", (e) => {
            e.preventDefault();

            let valid = true;
            const member = {
                domain: this.claim.data.domain,
                zero_zero_id: zero_zero_id.value.toUpperCase(),
                confirmation_code: confirmation_code.value,
            };

            clearMessage();

            // Validate guard fields
            [zero_zero_id, confirmation_code].forEach(function (input) {
                valid = validateField(input) && valid;
            });
            if (!valid) return;

            loadingGif(loading, "show");

            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
            this.claim.api
                .classMemberPost({}, member)
                .then((result) => {
                    // Check for error message
                    if (result.data.error) {
                        toggleFieldError(zero_zero_id, true, "");
                        toggleFieldError(confirmation_code, true, "");
                        this.errorHandler.log_exception(
                            new Error("Error fetching class member", {
                                cause: result,
                            })
                        );
                        this.errorHandler.display_modal(result);
                        return;
                    }

                    // Add notice id and confirmation code to claim data
                    this.exclusion.data.zero_zero_id = member.zero_zero_id;
                    this.exclusion.data.confirmation_code =
                        member.confirmation_code;

                    // Check for existing claim number and add to claim data
                    if (result.data.claim_id) {
                        this.exclusion.data.claim_id = result.data.claim_id;
                    }

                    // Hide guard
                    document.getElementById("guard").classList.add("d-none");

                    // New Claim Form
                    container.appendChild(this.exclusion_form(form));
                    container.scrollIntoView({ behavior: "smooth" });
                    outputDiv.appendChild(container);

                    loadingGif(loading, "hide", 250);

                    // Load member data into form
                    loadMember(result.data);

                    // Init claim fields
                    this.init_exclusion_fields(this.exclusion, form);

                    // Submit Exclusion
                    document
                        .getElementById("form")
                        .addEventListener("submit", (process) => {
                            this.process_exclusion(
                                process,
                                this.exclusion,
                                exclusion_deadline
                            );
                        });
                })
                .catch((error) => {
                    this.errorHandler.log_exception(
                        new Error("Error fetching class member", {
                            cause: error,
                        })
                    );
                    this.errorHandler.display_modal(error);
                });
        });
    }

    /**
     * exclusion_form()
     *
     * Generates an exclusion form for a claim with the given parameters.
     *
     * @param {object} params - The parameters for generating the form.
     * @returns {HTMLFormElement} The HTML form element containing the exclusion form.
     */
    exclusion_form(params) {
        // create the exclusion form
        const claimFormElems = [
            createElement(
                "h3",
                {
                    class: "text-center text-decoration-underline mb-3",
                },
                [],
                params.form_options.exclusion_form.title
            ),
            createElement(
                "div",
                {
                    class: "alert alert-info text-dark text-center fw-semibold mb-3",
                    id: "exclusion_form_top_notice",
                },
                [],
                i18n.getTranslation("exclusion_form_top_notice")
            ),
            createElement(
                "div",
                {
                    class: "text-center mb-3",
                },
                [],
                params.form_options.exclusion_form.body
            ),
            this.claim_contact_information(params),
        ];
        // if the claim fields were enabled push to form
        if (this.enableClaimFields) {
            claimFormElems.push(this.claim_fieldset(params));
        }
        // if the transational table is enabled, push to form
        if (this.enableClaimTransactions) {
            claimFormElems.push(this.claim_transactions(params));
        }
        // push the attestation to the form
        claimFormElems.push(
            this.claim_certification(params.form_options.exclusion_form)
        );
        // build the form and add elements
        const form = createElement(
            "form",
            {
                class: "needs-validation",
                id: "form",
                method: "post",
            },
            claimFormElems
        );
        // return the html form
        return form;
    }

    process_exclusion(e, exclusion, deadline) {
        console.log("cancelable?");
        console.log(e.cancelable);
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        // var exclusion = this.exclusion;
        if (exclusion.sent) {
            return;
        }

        clearMessage();

        exclusion.valid = true;

        this.exclusion.inputs = Array.from(
            document.querySelectorAll(
                "#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url"
            )
        );
        this.exclusion.inputs.transactions = Array.from(
            document.querySelectorAll(
                "#transactions input, #transactions textarea, #transactions select"
            )
        );
        exclusion.data.contact = {};
        exclusion.data.fields = {};

        exclusion.inputs.forEach(function (field) {
            if (field.hasAttribute("data-validate") && !validateField(field)) {
                exclusion.valid = false;
            } else if (field.type == "radio") {
                if (field.checked) {
                    exclusion.data[field.name] = field.value;
                    exclusion.data.contact[field.name] = field.value;
                }
            } else if (field.type == "checkbox") {
                if (field.checked) {
                    exclusion.data[field.name] = field.value;
                    exclusion.data.contact[field.name] = field.value;
                } else {
                    exclusion.data[field.name] = "No";
                    exclusion.data.contact[field.name] = "No";
                }
            } else {
                //} if (field.value) {
                exclusion.data[field.name] = field.value;
                exclusion.data.contact[field.name] = field.value;
            }
        });

        if (
            exclusion.inputs.transactions &&
            exclusion.inputs.transactions.length > 0
        ) {
            exclusion.inputs.transactions.forEach(function (field) {
                const label = field.getAttribute("data-label") || field.name;
                const item_id = field.getAttribute("item-id") || null;

                if (
                    field.hasAttribute("data-validate") &&
                    !validateField(field)
                ) {
                    exclusion.flags.valid = false;
                } else if (field.type == "radio") {
                    if (field.checked) {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] =
                                field.value;
                        } else {
                            exclusion.data.transactions[label] = field.value;
                        }
                    }
                } else if (field.type == "checkbox") {
                    if (field.checked) {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] =
                                field.value;
                        } else {
                            exclusion.data.transactions[label] = field.value;
                        }
                    } else {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] = "No";
                        } else {
                            exclusion.data.transactions[label] = "No";
                        }
                    }
                } else {
                    //} if (field.value) {
                    if (item_id) {
                        if (!exclusion.data.transactions[item_id]) {
                            exclusion.data.transactions[item_id] = {};
                        }
                        exclusion.data.transactions[item_id][label] =
                            field.value;
                    } else {
                        exclusion.data.transactions[label] = field.value;
                    }
                }
            });
        }

        if (!exclusion.valid) {
            const e = new ErrorHandler();
            e.display_modal(exclusion);
            return;
        }

        exclusion.sent = true;
        document.getElementById("submit-claim").disabled = true;

        loadingGif(loading, "show");

        exclusion.api
            .exclusionPost({}, exclusion.data)
            .then((result) => {
                if (result.data.error) {
                    loadingGif(loading, "hide", 250);
                    exclusion.success = false;
                    this.errorHandler.log_exception(
                        new Error("Error submitting exclusion", {
                            cause: result,
                        })
                    );
                    this.errorHandler.display_modal(result);
                    exclusion.inputs.forEach((field) => {
                        field.disabled = true;
                    });
                    return;
                }
                exclusion.success = true;
            })
            .catch((error) => {
                console.log(error);
                exclusion.success = false;
                this.errorHandler.log_exception(
                    new Error("Error submitting exclusion", { cause: error })
                );
                this.errorHandler.display_modal(error);
            })
            .finally(() => {
                loadingGif(loading, "hide", 250);

                if (exclusion.success) {
                    exclusion.data.confirmation =
                        this.exclusion_data_confirmation(exclusion.data);
                    document
                        .querySelector("div.display-form")
                        .classList.add("d-none");

                    outputDiv.appendChild(
                        this.exclusion_confirmation(exclusion.data)
                    );
                    outputDiv.scrollIntoView({ behavior: "smooth" });
                } else {
                    this.errorHandler.display_modal(result);
                }

                // Loop thru each input and disable to prevent changes and resubmissions
                exclusion.inputs.forEach((field) => {
                    field.disabled = true;
                });

                [].forEach.call(
                    document.querySelectorAll('input[type="radio"]'),
                    function (radio) {
                        radio.setAttribute("disabled", "disabled");
                    }
                );

                document
                    .getElementById("submit-claim")
                    .setAttribute("disabled", "disabled");
            });
    }

    exclusion_data_confirmation(exclusion) {
        const confirmation = {
            title: i18n.getTranslation("confirmation_exclusion_page")["title"],
            upload_fail: i18n.getTranslation("confirmation_exclusion_page")[
                "upload_fail"
            ],
            print_note: i18n.getTranslation("confirmation_exclusion_page")[
                "print_note"
            ],
            claim_details: {
                title: i18n.getTranslation("confirmation_exclusion_page")[
                    "claim_details"
                ]["title"],
                body: i18n.getTranslation("confirmation_exclusion_page")[
                    "claim_details"
                ]["body"],
                footer: i18n.getTranslation("confirmation_exclusion_page")[
                    "claim_details"
                ]["footer"],
                edit: i18n.getTranslation("confirmation_exclusion_page")[
                    "claim_details"
                ]["edit"],
                excluded_keys: [],
            },
        };
        return confirmation;
    }

    /**
     * Creates an instance of OptOut.
     *
     * @param {Object} params - The parameters for creating the instance.
     * @param {string} params.appClaimForm - The path to the app claim form.
     * @param {string} params.userLanguage - The user's language.
     * @param {I18n} params.i18n - The i18n object.
     * @param {boolean?} params.enableClaimFields - Whether to enable claim fields.
     * @param {boolean?} params.enableClaimTransactions - Whether to enable claim transactions.
     * @param {ErrorHandler?} params.errorHandler - The error handler.
     * @returns {OptOut} - The created instance.
     */
    static createInstance({
        appClaimForm,
        userLanguage,
        i18n,
        enableClaimFields = false,
        enableClaimTransactions = false,
        errorHandler = new ErrorHandler(),
    }) {
        return new OptOut(
            appClaimForm,
            userLanguage,
            i18n,
            enableClaimFields,
            enableClaimTransactions,
            errorHandler
        );
    }
}
